import { Component, inject, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { filter, from, map, Observable, switchMap } from 'rxjs';
import manifest from '../../../manifest.json';
import { AppRoute } from '../../app-route';
import { AppState } from '../../app-state/app.state';
import { Connect3dPanelService } from '../../connect/connect-3d-panel.service';
import { ConnectExplorerService } from '../../connect/connect-explorer.service';
import { ConnectPanelService } from '../../connect/connect-panel.service';
import { ConnectCommand } from '../../connect/connect.models';
import { ConnectService } from '../../connect/connect.service';
import { createGeospatialMenu } from './ext-host.menu';

@UntilDestroy()
@Component({
  selector: 'sd-ext-host',
  imports: [RouterModule],
  templateUrl: './ext-host.component.html',
  styleUrls: ['./ext-host.component.scss'],
})
export class ExtHostComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly connectService = inject(ConnectService);
  private readonly connectPanelService = inject(ConnectPanelService);
  private readonly connect3dPanelService = inject(Connect3dPanelService);
  private readonly connectExplorerService = inject(ConnectExplorerService);

  private command$: Observable<string> = this.getCommandStream();

  async ngOnInit(): Promise<void> {
    await this.setExtensionMenu();

    this.subscribeToConfigCommand();
    this.subscribeToExtensionCommand();

    this.connectPanelService.subscribeToConnectEvents();
    this.connect3dPanelService.subscribeToConnectEvents();
    this.connectExplorerService.subscribeForClosingPrompt();
  }

  private getCommandStream() {
    return from(this.connectService.getWorkspace()).pipe(
      switchMap((workspace) => workspace.command$),
      map((event) => event.data as string),
    );
  }

  private async setExtensionMenu() {
    // LM: Connect Workspace API is the same for file view and 3d view.
    // setMenu throws an error if called in Connect 3d view
    try {
      const workspace = await this.connectService.getWorkspace();
      await workspace.api.ui.setMenu(
        createGeospatialMenu(this.store.selectSnapshot(AppState.settings)),
      );
    } catch {}
  }

  private subscribeToConfigCommand() {
    this.command$
      .pipe(
        filter((command) => command === manifest.configCommand),
        untilDestroyed(this),
      )
      .subscribe(() => this.router.navigate([AppRoute.Config]));
  }

  private subscribeToExtensionCommand() {
    // When the extension menu item is clicked on the Connect side nav panel,
    // navigate to the scan list route only if the current route is config.
    this.command$
      .pipe(
        filter((command) => command === ConnectCommand.ScandataBrowser),
        filter(() => this.router.url.startsWith(`/${AppRoute.Config}`)),
        untilDestroyed(this),
      )
      .subscribe(() => this.router.navigate([''], { onSameUrlNavigation: 'reload' }));
  }
}
